/**
 * 路由参数名, 生成/清理地址时需要与当前地址栏中的参数隔离开
 */
export const ROUTE_PARAM_KEYS = ['contentId', 'categoryId', 'page'];

/**
 * 判断跳转字段是否为绝对地址
 *
 * 内容条目的 jump_link 有两种形态:
 * 1. 枚举值: content / link / stay, 需要前端自行拼地址;
 * 2. CMS 里直接配置的完整链接(如置顶Banner), 此时不能再拿 item.contentId 拼详情页,
 *    否则会跳到 Banner 自身那条空内容上.
 *
 * @param {string} value - jump_link 原始值
 * @returns {boolean}
 */
export function isAbsoluteUrl(value) {
  return typeof value === 'string' && /^(https?:)?\/\//i.test(value.trim());
}

/**
 * 过滤掉空值后生成 URLSearchParams
 * @param {object} params - 参数对象
 * @returns {URLSearchParams}
 */
const toSearchParams = (params = {}) => {
  const searchParams = new URLSearchParams();
  Object.keys(params).forEach(key => {
    const value = params[key];
    if (value !== undefined && value !== null && value !== '') {
      searchParams.set(key, value);
    }
  });
  return searchParams;
};

/**
 * 生成 hash 路由的跳转地址
 *
 * 项目使用 HashRouter, 路由参数必须拼在 hash 内部: #/path?key=value,
 * 拼在 hash 之前(?key=value#/path)会被当成服务端请求参数, 前端路由读不到.
 * 同时保留地址栏中原有的非路由参数(如 t、utm_source), 避免统计参数丢失.
 *
 * @param {string} path - 路由路径, 如 /strategy-detail
 * @param {object} [params] - 路由参数
 * @returns {string} 完整跳转地址
 * @example
 * buildRouteUrl('/strategy-detail', { contentId: '1', categoryId: 'ds_cat_1', page: 1 })
 * // => https://xxx.com/index.html?t=123#/strategy-detail?contentId=1&categoryId=ds_cat_1&page=1
 */
export default function buildRouteUrl(path, params = {}) {
  const { origin, pathname, search } = window.location;

  // 清掉地址栏里残留的路由参数, 防止与目标页参数重复
  const searchParams = new URLSearchParams(search);
  ROUTE_PARAM_KEYS.forEach(key => searchParams.delete(key));

  const searchStr = searchParams.toString();
  const queryStr = toSearchParams(params).toString();

  return `${origin}${pathname}${searchStr ? `?${searchStr}` : ''}#${path}${queryStr ? `?${queryStr}` : ''}`;
}

/**
 * 清理当前地址中(含 hash 内部)的路由参数, 不触发页面刷新
 * @returns {void}
 */
export function clearRouteParams() {
  const { origin, pathname, search, hash } = window.location;
  const searchParams = new URLSearchParams(search);

  const [hashPath, hashQuery] = hash.replace(/^#/, '').split('?');
  const hashParams = new URLSearchParams(hashQuery || '');

  ROUTE_PARAM_KEYS.forEach(key => {
    searchParams.delete(key);
    hashParams.delete(key);
  });

  const searchStr = searchParams.toString();
  const hashQueryStr = hashParams.toString();
  const nextUrl = `${origin}${pathname}${searchStr ? `?${searchStr}` : ''}#${hashPath}${hashQueryStr ? `?${hashQueryStr}` : ''}`;

  window.history.replaceState(window.history.state, document.title, nextUrl);
}
